import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../services/supabaseClient';
import type { Store, StoreContextType } from '../types/store';

const StoreContext = createContext<StoreContextType | undefined>(undefined);

interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [userStoreId, setUserStoreId] = useState<string | null>(null);
  const [canViewAllStores, setCanViewAllStores] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Charger les magasins et déterminer les permissions
  const loadStores = async () => {
    if (!isAuthenticated || !user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Récupérer l'auth_user_id depuis la session Supabase
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!authUser) {
        setIsLoading(false);
        return;
      }

      // 1. Récupérer le store_id de l'utilisateur et son rôle
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(
          `
          store_id,
          role_id,
          roles!inner (
            nom
          )
        `
        )
        .eq('auth_user_id', authUser.id)
        .single();

      if (userError) {
        setIsLoading(false);
        return;
      }

      const roleName = (userData?.roles as any)?.nom || user.role;
      const storeId = userData?.store_id;

      setUserStoreId(storeId);

      // 2. Déterminer si l'utilisateur peut voir tous les magasins
      const isSuperAdmin = user.email === 'daniel.gosset@hotmail.com';
      const isAdmin = roleName === 'admin' || roleName === 'direction';
      const canViewAll = isSuperAdmin || isAdmin;

      setCanViewAllStores(canViewAll);

      // 3. Charger tous les magasins actifs
      const { data: stores, error: storesError } = await supabase
        .from('stores')
        .select('*')
        .eq('active', true)
        .order('ordre', { ascending: true });

      if (storesError) {
        setIsLoading(false);
        return;
      }

      setAllStores(stores || []);

      // 4. Définir le magasin actuel
      if (canViewAll) {
        // Admin/Super Admin : par défaut, voir tous les magasins (currentStore = null)
        // Ou charger le premier magasin de la liste
        const storedStoreId = localStorage.getItem('selected_store_id');
        if (storedStoreId) {
          const selectedStore = stores?.find(s => s.id === storedStoreId);
          setCurrentStore(selectedStore || null);
        } else {
          setCurrentStore(null); // null = voir tous les magasins
        }
      } else {
        // Utilisateur normal : magasin assigné
        if (storeId) {
          const userStore = stores?.find(s => s.id === storeId);
          setCurrentStore(userStore || null);
        }
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  // Charger les magasins au montage et quand l'utilisateur change
  useEffect(() => {
    loadStores();
  }, [isAuthenticated, user?.id]);

  // Changer de magasin (pour Admin/Super Admin)
  const switchStore = (storeId: string) => {
    if (!canViewAllStores) {
      return;
    }

    if (storeId === 'all') {
      setCurrentStore(null);
      localStorage.removeItem('selected_store_id');
    } else {
      const store = allStores.find(s => s.id === storeId);
      if (store) {
        setCurrentStore(store);
        localStorage.setItem('selected_store_id', storeId);
      }
    }
  };

  // Rafraîchir la liste des magasins
  const refreshStores = async () => {
    await loadStores();
  };

  const value: StoreContextType = {
    currentStore,
    allStores,
    userStoreId,
    canViewAllStores,
    isLoading,
    switchStore,
    refreshStores,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

// Hook personnalisé pour utiliser le contexte
export const useStore = (): StoreContextType => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};
